import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from 'shell/components/Box';
import Button from 'shell/components/Button';
import { Dialog, DialogActions, DialogContent } from 'shell/components/Dialog';
import Divider from 'shell/components/Divider';
import Icon from 'shell/components/Icon';
import IconButton from 'shell/components/IconButton';
import Stack from 'shell/components/Stack';
import Text from 'shell/components/Text';
import TextArea from 'shell/components/TextArea';
import {
    hasEvidenceSuggestion,
    isEvidenceInstructionTooLong,
    toggleEvidenceSuggestion,
} from 'utils/assignmentSubmissionTypes';
import { CUSTOM_CONTENT_WORD_COUNT_LIMIT } from 'utils/constants';

export const EVIDENCE_TYPE_DETAILS = {
    documents: {
        label: 'Documents',
        icon: 'fileTypeUndefined',
        description: '.doc, .docx, .pdf, text entry (including typed and handwritten)',
        title: 'What the AI flags in document submissions',
        flag: ['Claims with no attribution', 'Thesis not stated', 'Quoted text without citation', 'File problems'],
        ignore: [
            'Regional spelling or dialect',
            'Citation style variants',
            'Heading and formatting choices',
            'Paragraph length',
            'Sentence variety',
        ],
    },
    videos: {
        label: 'Videos',
        icon: 'video',
        description: '.mp4, .mpg, .mpeg, .mov (including video services)',
        title: 'What the AI flags in video submissions',
        flag: ['Claims with no attribution', 'Inaudible speech', 'Missing required demonstration', 'File problems'],
        ignore: ['Accent or dialect', 'Camera and background choices', 'Speaking pace', 'Editing and transitions'],
    },
    slides: {
        label: 'Slides',
        icon: 'presentation',
        description: '.pptx, .ppt, .pdf',
        title: 'What the AI flags in slide submissions',
        flag: ['A claim on a slide with no source', 'Slide never spoken to'],
        ignore: ['Template and theme choice', 'Font and colour choices', 'Slide count', 'Animations and transitions'],
    },
    spreadsheets: {
        label: 'Spreadsheets',
        icon: 'spreadsheet',
        description: '.xlsx, .csv (including formulae, values, and graphs)',
        title: 'What the AI flags in spreadsheet submissions',
        flag: ['A hardcoded value where a formula was asked for', 'Broken references', 'File problems'],
        ignore: ['Cell formatting and colour', 'Column widths', 'Sheet naming', 'Number of helper columns'],
    },
};

const EvidencePreferencesDialog = ({ type, preferences, open = true, disabled = false, onClose, onSave }) => {
    const { t } = useTranslation('assignmentSettings');
    const titleId = useId();
    const descriptionId = useId();
    const [draft, setDraft] = useState({ flag_instructions: '', ignore_instructions: '' });
    const config = EVIDENCE_TYPE_DETAILS[type];

    useEffect(() => {
        if (open)
            setDraft({
                flag_instructions: preferences?.flag_instructions || '',
                ignore_instructions: preferences?.ignore_instructions || '',
            });
    }, [open, preferences]);

    if (!config) return null;
    const hasErrors = Object.values(draft).some(isEvidenceInstructionTooLong);
    const save = () => {
        if (!disabled && !hasErrors) onSave(draft);
    };

    return (
        <Dialog
            open={open}
            scroll={'paper'}
            maxWidth={false}
            aria-labelledby={titleId}
            aria-describedby={descriptionId}
            PaperProps={{ sx: { width: 838, maxWidth: 'calc(100% - 32px)', m: 4 } }}
            onClose={onClose}
        >
            <Stack sx={{ px: { mobile: 4, tablet: 7 }, py: 3 }} spacing={1}>
                <Stack direction={'row'} alignItems={'center'} spacing={3}>
                    <Text id={titleId} component={'h2'} variant={'h6'} weight={'medium'} sx={{ flex: 1 }}>
                        {t(`evidence.types.${type}.title`, config.title)}
                    </Text>
                    <IconButton
                        aria-label={t('evidence.close', 'Close evidence preferences')}
                        kind={'exit'}
                        size={'medium'}
                        variant={'square'}
                        onClick={onClose}
                    />
                </Stack>
                <Text id={descriptionId} color={'light'}>
                    {t(
                        'evidence.explanation',
                        'These decide what the AI brings you as evidence. They never decide the score, the rubric does.'
                    )}
                </Text>
            </Stack>
            <Divider />
            <DialogContent sx={{ px: { mobile: 4, tablet: 7 }, py: 3 }}>
                <Stack spacing={6}>
                    <Stack
                        direction={'row'}
                        alignItems={'center'}
                        spacing={3}
                        sx={{
                            alignSelf: 'flex-start',
                            borderRadius: '40px',
                            px: 3,
                            py: 2,
                            background: (theme) => theme.palette.ensightful.primary[50],
                        }}
                    >
                        <Icon
                            kind={config.icon}
                            sx={{ fontSize: '1.5rem', color: (theme) => theme.palette.ensightful.neutral[700] }}
                        />
                        <Text weight={'medium'}>{t(`evidence.types.${type}.label`, config.label)}</Text>
                    </Stack>
                    {['flag', 'ignore'].map((kind, index) => {
                        const field = `${kind}_instructions`;
                        const label =
                            kind === 'flag'
                                ? t('evidence.flag-label', 'Should the AI flag these items?')
                                : t('evidence.ignore-label', 'What the AI should not flag');
                        const error = isEvidenceInstructionTooLong(draft[field]);
                        return (
                            <Stack key={kind} spacing={3}>
                                {index > 0 && <Divider sx={{ mb: 3 }} />}
                                <TextArea
                                    id={`${titleId}-${kind}`}
                                    label={label}
                                    aria-label={label}
                                    inputProps={{ 'aria-label': label, 'aria-invalid': error }}
                                    rows={6}
                                    value={draft[field]}
                                    error={error}
                                    disabled={disabled}
                                    helperText={
                                        error
                                            ? t(
                                                  'evidence.length-error',
                                                  'Use no more than {{limit}} words and 100,000 characters.',
                                                  { limit: CUSTOM_CONTENT_WORD_COUNT_LIMIT }
                                              )
                                            : undefined
                                    }
                                    sx={{
                                        '& textarea': { padding: '12px 16px' },
                                        '& label': { fontWeight: 500, mb: 2 },
                                    }}
                                    onChange={(event) =>
                                        setDraft((current) => ({ ...current, [field]: event.target.value }))
                                    }
                                />
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                    {config[kind].map((fallback, suggestionIndex) => {
                                        const suggestion = t(
                                            `evidence.types.${type}.${kind}.${suggestionIndex}`,
                                            fallback
                                        );
                                        const selected = hasEvidenceSuggestion(draft[field], suggestion);
                                        return (
                                            <Button
                                                key={suggestionIndex}
                                                aria-pressed={selected}
                                                color={selected ? 'primary' : 'neutral'}
                                                variant={'secondary'}
                                                size={'medium'}
                                                disabled={disabled}
                                                sx={{
                                                    maxWidth: '100%',
                                                    px: 3,
                                                    py: 2,
                                                    textAlign: 'left',
                                                    ...(selected && {
                                                        background: (theme) => theme.palette.ensightful.primary[50],
                                                    }),
                                                }}
                                                onClick={() =>
                                                    setDraft((current) => ({
                                                        ...current,
                                                        [field]: toggleEvidenceSuggestion(current[field], suggestion),
                                                    }))
                                                }
                                            >
                                                {suggestion}
                                            </Button>
                                        );
                                    })}
                                </Box>
                            </Stack>
                        );
                    })}
                </Stack>
            </DialogContent>
            <Divider />
            <DialogActions sx={{ gap: 2.5, px: { mobile: 4, tablet: 7 } }}>
                <Button color={'neutral'} variant={'tertiary'} size={'medium'} onClick={onClose}>
                    {t('evidence.cancel', 'Cancel')}
                </Button>
                <Button size={'medium'} disabled={disabled || hasErrors} onClick={save}>
                    {t('evidence.done', 'Done')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EvidencePreferencesDialog;
